import { z } from 'zod'

/* Spec COG-005 — contract of the panel API (/api/v1). Server and screens share these schemas and types.
   Responses never carry engine ids, route names or engine vocabulary: only what the screens show. */

export const API_BASE = '/api/v1'

/** Agent colour key, the same keys as the design tokens (--rbg, --rbo, ...). */
export const AGENT_COLORS = ['g', 'o', 'b', 'v', 'p', 't', 'y'] as const
export type AgentColor = (typeof AGENT_COLORS)[number]

/** What the agent is doing now. Drives the Francisco mood on screen. */
export const AGENT_ACTIVITIES = ['resting', 'working', 'thinking', 'waiting', 'problem', 'paused'] as const
export type AgentActivity = (typeof AGENT_ACTIVITIES)[number]

export const agentSchema = z.object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string(),
    color: z.enum(AGENT_COLORS),
    paused: z.boolean(),
    activity: z.enum(AGENT_ACTIVITIES),
    /** Short status word for the screen, e.g. "Trabalhando", "Pausado". */
    statusLabel: z.string(),
    /** One-line role, e.g. "Lê fichas e cobra". */
    roleShort: z.string(),
    /** Systems this agent uses (connection slugs). */
    uses: z.array(z.string()),
    /** True while the agent is example data (not defined in the engine yet). */
    example: z.boolean(),
})
export type Agent = z.infer<typeof agentSchema>

export const agentRoleSchema = z.object({
    slug: z.string(),
    goal: z.string(),
    does: z.array(z.string()),
    never: z.array(z.string()),
    uses: z.array(z.string()),
    /** Where the definition came from, e.g. "agents/captacao-por-ficha/AGENTS.md", or null for example agents. */
    source: z.string().nullable(),
})
export type AgentRole = z.infer<typeof agentRoleSchema>

export const PENDING_KINDS = ['form_review', 'approval', 'question', 'moderation'] as const
export type PendingKind = (typeof PENDING_KINDS)[number]

export const pendingItemSchema = z.object({
    id: z.string(),
    kind: z.enum(PENDING_KINDS),
    /** e.g. "Lúcia · CPF ilegível". */
    title: z.string(),
    /** e.g. "R$ 80/mês", or null. */
    detail: z.string().nullable(),
    agentSlug: z.string(),
    /** ISO date-time. */
    createdAt: z.string(),
})
export type PendingItem = z.infer<typeof pendingItemSchema>

export const CONNECTION_STATUSES = ['ok', 'testing', 'waiting', 'down', 'unknown'] as const
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number]

export const connectionSchema = z.object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string(),
    /** e.g. "Pagamentos · sandbox". */
    description: z.string(),
    status: z.enum(CONNECTION_STATUSES),
    /** Word shown on screen: "Funcionando", "Em testes", "Aguardando", "Sem resposta". */
    statusLabel: z.string(),
    /** Why it is waiting/down, in plain words, or null. */
    note: z.string().nullable(),
    latencyMs: z.number().int().nullable(),
    checkedAt: z.string().nullable(),
    /** Agent slugs that use this system. */
    usedBy: z.array(z.string()),
})
export type Connection = z.infer<typeof connectionSchema>

export const connectionTestSchema = z.object({
    slug: z.string(),
    ok: z.boolean(),
    latencyMs: z.number().int().nullable(),
    /** e.g. "OK 385 ms", "Sem resposta", "403 · aguardando". */
    label: z.string(),
})
export type ConnectionTest = z.infer<typeof connectionTestSchema>

export const NOTICE_LEVELS = ['info', 'attention', 'problem'] as const
export const noticeSchema = z.object({
    id: z.string(),
    level: z.enum(NOTICE_LEVELS),
    text: z.string(),
})
export type Notice = z.infer<typeof noticeSchema>

export const overviewSchema = z.object({
    agents: z.array(agentSchema),
    pending: z.array(pendingItemSchema),
    connections: z.array(connectionSchema),
    notices: z.array(noticeSchema),
    /** "example" while the panel shows example data; "live" when it reads the real systems. */
    dataMode: z.enum(['example', 'live']),
})
export type Overview = z.infer<typeof overviewSchema>

export const meSchema = z.object({
    name: z.string(),
    email: z.string(),
})
export type Me = z.infer<typeof meSchema>

/* ── Phase 2: "Precisa de você" and actions ───────────────────────────────── */

/** approve/reject: approval requests. return_to_agent: send a form in review back to the agent with a note.
 *  close: mark it handled by a person (with a note). */
export const PENDING_ACTIONS = ['approve', 'reject', 'return_to_agent', 'close'] as const
export type PendingAction = (typeof PENDING_ACTIONS)[number]

export const pendingDetailSchema = z.object({
    item: pendingItemSchema,
    agentName: z.string(),
    /** Plain reason, e.g. "CPF ilegível na foto da ficha". */
    reason: z.string(),
    /** Evidence rows shown on the card, e.g. { label: "Valor", value: "R$ 80/mês" }. Never CPF, e-mail, phone or card. */
    evidence: z.array(z.object({ label: z.string(), value: z.string() })),
    /** Actions the panel may take on this item (empty = only view). */
    actions: z.array(z.enum(PENDING_ACTIONS)),
    /** Last message the agent left on this item, already translated, or null. */
    agentNote: z.object({ at: z.string(), text: z.string() }).nullable(),
})
export type PendingDetail = z.infer<typeof pendingDetailSchema>

export const decisionSchema = z.object({ note: z.string().trim().max(500).optional() })
export type DecisionInput = z.infer<typeof decisionSchema>

export const actionResultSchema = z.object({ ok: z.boolean(), message: z.string() })
export type ActionResult = z.infer<typeof actionResultSchema>

/* ── Phase 3: Fichas and Ao Vivo ──────────────────────────────────────────── */

export const FORM_STATUSES = ['received', 'charged', 'in_review', 'error'] as const
export type FormStatus = (typeof FORM_STATUSES)[number]

export const formSchema = z.object({
    protocol: z.string(),
    /** First name only, or null when the panel does not show names (decision §9.1). */
    firstName: z.string().nullable(),
    /** Monthly amount in BRL. */
    amount: z.number(),
    status: z.enum(FORM_STATUSES),
    statusLabel: z.string(),
    /** ISO date-time the form was received. */
    receivedAt: z.string(),
    /** e.g. "Recorrência todo dia 10" or "Cartão recusado", or null. */
    note: z.string().nullable(),
})
export type Form = z.infer<typeof formSchema>

export const formsPageSchema = z.object({
    items: z.array(formSchema),
    total: z.number().int(),
    page: z.number().int(),
    pages: z.number().int(),
})
export type FormsPage = z.infer<typeof formsPageSchema>

export const formsSummarySchema = z.object({
    /** e.g. "Último Live · sáb 11/10". */
    periodLabel: z.string(),
    total: z.number().int(),
    charged: z.number().int(),
    inReview: z.number().int(),
    withError: z.number().int(),
    /** New monthly donations in BRL. */
    monthlyNew: z.number(),
})
export type FormsSummary = z.infer<typeof formsSummarySchema>

export const LIVE_LEVELS = ['info', 'success', 'attention', 'problem'] as const
export const liveEventSchema = z.object({
    id: z.string(),
    at: z.string(),
    agentSlug: z.string(),
    /** Already translated, e.g. "Cobrou a ficha de Maria · R$ 160/mês". */
    text: z.string(),
    level: z.enum(LIVE_LEVELS),
})
export type LiveEvent = z.infer<typeof liveEventSchema>

export const liveSchema = z.object({
    agents: z.array(agentSchema),
    events: z.array(liveEventSchema),
    summary: formsSummarySchema,
})
export type Live = z.infer<typeof liveSchema>

/* ── Phase 4: Memória and Pergunte ────────────────────────────────────────── */

export const memoryDocumentSchema = z.object({
    id: z.string(),
    title: z.string(),
    /** e.g. "politica_formal". */
    kind: z.string(),
    /** e.g. "Política". */
    kindLabel: z.string(),
    chunks: z.number().int(),
    updatedAt: z.string().nullable(),
})
export type MemoryDocument = z.infer<typeof memoryDocumentSchema>

export const memoryQuerySchema = z.object({
    at: z.string(),
    /** Agent that asked, or null when unknown. */
    agentSlug: z.string().nullable(),
    /** Truncated question (max 200 chars). */
    question: z.string(),
    /** Titles of the documents cited. */
    sources: z.array(z.string()),
})
export type MemoryQuery = z.infer<typeof memoryQuerySchema>

export const memorySchema = z.object({
    totals: z.object({ documents: z.number().int(), chunks: z.number().int(), queriesToday: z.number().int() }),
    documents: z.array(memoryDocumentSchema),
    queries: z.array(memoryQuerySchema),
})
export type Memory = z.infer<typeof memorySchema>

export const askSchema = z.object({
    /** "assistente" or an agent slug. */
    agentSlug: z.string().regex(/^[a-z0-9-]+$/),
    question: z.string().trim().min(2).max(1000),
})
export type AskInput = z.infer<typeof askSchema>

export const answerSchema = z.object({
    agentSlug: z.string(),
    answer: z.string(),
    sources: z.array(z.object({ title: z.string(), location: z.string().nullable() })),
    /** Phase 4b: the question went to the agent as a task; the reply comes later on GET /ask/:ticket. */
    ticket: z.string().optional(),
})
export type Answer = z.infer<typeof answerSchema>

/** Phase 4b: where a question sent to an agent stands. */
export const askReplySchema = z.object({
    /** waiting = no reply yet; answered = the agent replied; gone = the task was closed without a reply. */
    state: z.enum(['waiting', 'answered', 'gone']),
    /** The agent is working on it right now (only while waiting). */
    working: z.boolean(),
    /** The agent's reply, already cleaned (answered only). */
    answer: z.string().nullable(),
    at: z.string().nullable(),
})
export type AskReply = z.infer<typeof askReplySchema>

/* ── Phase 6: memory import, system health, review and live extras ─────────── */

export const MEMORY_DOCUMENT_STATUSES = ['indexing', 'indexed', 'failed'] as const
export type MemoryDocumentStatus = (typeof MEMORY_DOCUMENT_STATUSES)[number]

/** A memory source kind as the panel shows it (weight 1–5 = how much it counts in the search). */
export const memoryKindSchema = z.object({ id: z.string(), label: z.string(), weight: z.number().int().min(1).max(5) })
export type MemoryKind = z.infer<typeof memoryKindSchema>

export const memoryUploadItemSchema = z.object({
    id: z.string(),
    title: z.string(),
    kind: z.string(),
    kindLabel: z.string(),
    status: z.enum(MEMORY_DOCUMENT_STATUSES),
    chunks: z.number().int(),
    /** Display name of who sent it (first name), or null. */
    uploadedBy: z.string().nullable(),
    uploadedAt: z.string().nullable(),
    /** e.g. "pdf". */
    extension: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    /** Plain PT-BR reason when failed, or null. */
    error: z.string().nullable(),
    /** Plain PT-BR warnings, e.g. "Texto quase vazio: parece um PDF escaneado". */
    warnings: z.array(z.string()),
    /** True when it replaced an older version with the same title and kind. */
    replaced: z.boolean(),
})
export type MemoryUploadItem = z.infer<typeof memoryUploadItemSchema>

/* Zip import (RAG batches.py): the zip goes up in parts, the memory lists its files (ready = preview: pick the kind
   and the name of each folder), then imports one file at a time (running) until done; or canceled / failed. */
export const MEMORY_BATCH_STATUSES = ['receiving', 'ready', 'running', 'done', 'canceled', 'failed'] as const
export type MemoryBatchStatus = (typeof MEMORY_BATCH_STATUSES)[number]
export const MEMORY_BATCH_ITEM_STATUSES = ['pending', 'skipped', 'processing', 'indexed', 'failed'] as const
export type MemoryBatchItemStatus = (typeof MEMORY_BATCH_ITEM_STATUSES)[number]

export const memoryBatchItemSchema = z.object({
    n: z.number().int(),
    /** Path inside the zip. */
    path: z.string(),
    /** Top folder it belongs to ("" = loose files). */
    folder: z.string(),
    sizeBytes: z.number().int(),
    status: z.enum(MEMORY_BATCH_ITEM_STATUSES),
    /** Plain PT-BR reason when skipped or failed. */
    reason: z.string().nullable(),
    /** Title it gets in the memory (once started). */
    title: z.string().nullable(),
    warnings: z.array(z.string()),
})
export type MemoryBatchItem = z.infer<typeof memoryBatchItemSchema>

export const memoryBatchFolderSchema = z.object({
    folder: z.string(),
    /** Files that will be imported. */
    files: z.number().int(),
    /** Files left out (format, size, duplicate title). */
    skipped: z.number().int(),
    /** Name that opens the title of its files (suggested until started). */
    name: z.string(),
})
export type MemoryBatchFolder = z.infer<typeof memoryBatchFolderSchema>

export const memoryBatchSchema = z.object({
    id: z.string(),
    name: z.string(),
    sizeBytes: z.number().int(),
    receivedBytes: z.number().int(),
    /** Size of each part to send (the last one is smaller). */
    partBytes: z.number().int(),
    uploadedBy: z.string().nullable(),
    status: z.enum(MEMORY_BATCH_STATUSES),
    kind: z.string().nullable(),
    /** Single top folder of the zip, left out of the titles ("" = none). */
    root: z.string(),
    error: z.string().nullable(),
    createdAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    counts: z.object({ total: z.number().int(), indexed: z.number().int(), failed: z.number().int(), pending: z.number().int(), skipped: z.number().int() }),
    /** Processed files over the files that go in, 0–100. */
    percent: z.number().int().min(0).max(100),
    /** Title (or path) of the file being imported now. */
    current: z.string().nullable(),
    /** Empty in lists. */
    folders: z.array(memoryBatchFolderSchema),
    items: z.array(memoryBatchItemSchema),
})
export type MemoryBatch = z.infer<typeof memoryBatchSchema>

export const memoryBatchPartSchema = z.object({ receivedBytes: z.number().int(), sizeBytes: z.number().int() })
export type MemoryBatchPart = z.infer<typeof memoryBatchPartSchema>

export const memoryBatchCreateSchema = z.object({ name: z.string().min(1).max(200), sizeBytes: z.number().int().positive() })
export type MemoryBatchCreate = z.infer<typeof memoryBatchCreateSchema>
export const memoryBatchStartSchema = z.object({ kind: z.string().min(1), names: z.record(z.string(), z.string().max(80)) })
export type MemoryBatchStart = z.infer<typeof memoryBatchStartSchema>

export const memoryImportSchema = z.object({
    kinds: z.array(memoryKindSchema),
    recent: z.array(memoryUploadItemSchema),
    /** Accepted extensions and max size, for the drop zone. */
    accept: z.object({ extensions: z.array(z.string()), maxBytes: z.number().int() }),
    /** False when the panel cannot send documents (memory not configured for writing). */
    enabled: z.boolean(),
    /** Zip import: max zip size, or null when the memory does not take zips yet. */
    zip: z.object({ maxBytes: z.number().int() }).nullable(),
    /** Recent zip imports, newest first (no items), so an import still running shows again after a reload. */
    batches: z.array(memoryBatchSchema),
})
export type MemoryImport = z.infer<typeof memoryImportSchema>

export const HEALTH_ACCESS = ['R', 'W', 'RW', 'IA'] as const
export type HealthAccess = (typeof HEALTH_ACCESS)[number]

export const healthPointSchema = z.object({ at: z.string(), ok: z.boolean(), latencyMs: z.number().int().nullable() })
export type HealthPoint = z.infer<typeof healthPointSchema>

export const connectionHealthSchema = z.object({
    slug: z.string(),
    /** Share of successful checks in the last 30 days (0–1), or null without history. */
    uptime30d: z.number().min(0).max(1).nullable(),
    /** Last checks, oldest first (max 24; ~12 min with the default 30 s interval). */
    points: z.array(healthPointSchema),
    access: z.enum(HEALTH_ACCESS).nullable(),
})
export type ConnectionHealth = z.infer<typeof connectionHealthSchema>

export const checklistItemSchema = z.object({
    id: z.string(),
    text: z.string(),
    done: z.boolean(),
    doneBy: z.string().nullable(),
    doneAt: z.string().nullable(),
})
export type ChecklistItem = z.infer<typeof checklistItemSchema>

export const connectionDetailSchema = z.object({
    connection: connectionSchema,
    health: connectionHealthSchema,
    /** What the agents may do in this system, e.g. { mode: 'W', text: 'Cobrar a 1ª mensalidade' }. */
    permissions: z.array(z.object({ mode: z.enum(['R', 'W', 'IA']), text: z.string() })),
    /** Last calls/checks, newest first (max 10), already scrubbed (no paths, ids or keys). */
    log: z.array(z.object({ at: z.string(), ok: z.boolean(), text: z.string() })),
    /** "Para liberar": steps a person must do so this system works. */
    checklist: z.array(checklistItemSchema),
})
export type ConnectionDetail = z.infer<typeof connectionDetailSchema>

export const checklistAddSchema = z.object({ text: z.string().trim().min(3).max(200) })
export const checklistToggleSchema = z.object({ done: z.boolean() })

export const historyEntrySchema = z.object({
    at: z.string(),
    who: z.enum(['agent', 'person', 'system']),
    /** Agent slug or person first name, or null. */
    by: z.string().nullable(),
    text: z.string(),
})
export type HistoryEntry = z.infer<typeof historyEntrySchema>

export const pendingExtrasSchema = z.object({
    /** Form protocol (FD-…) when the item is about a form, or null. */
    protocol: z.string().nullable(),
    /** When the responsible person was e-mailed about it, or null. */
    mailSentAt: z.string().nullable(),
    /** Full history, oldest first (max 20), scrubbed. */
    history: z.array(historyEntrySchema),
})
export type PendingExtras = z.infer<typeof pendingExtrasSchema>

export const decisionLogEntrySchema = z.object({
    at: z.string(),
    /** e.g. "Aprovou", "Devolveu ao agente", "Pausou". */
    action: z.string(),
    /** e.g. "Ficha FD-… · Lúcia" or the agent name. */
    target: z.string(),
    ok: z.boolean(),
})
export type DecisionLogEntry = z.infer<typeof decisionLogEntrySchema>

export const liveExtrasSchema = z.object({
    /** Capture pipeline counts for the period, or null when the Hub is not configured. */
    pipeline: z.object({
        received: z.number().int(),
        sentToEngine: z.number().int(),
        charged: z.number().int(),
        inReview: z.number().int(),
        withError: z.number().int(),
    }).nullable(),
    /** Memory answers today (RAG searches), or null. */
    memoryAnswersToday: z.number().int().nullable(),
    /** One line per agent about today, e.g. { agentSlug, text: "23 perguntas, 19 resolvidas" }. */
    othersToday: z.array(z.object({ agentSlug: z.string(), text: z.string() })),
})
export type LiveExtras = z.infer<typeof liveExtrasSchema>

/* ── Phase 6b: agent flows (Ao Vivo subway lanes and big flow map) ───────── */

export const flowStationSchema = z.object({
    id: z.string(),
    /** e.g. "Foto", "Leitura IA", "Hub · protegido", "Agente decide", "Cobrada". */
    label: z.string(),
    /** Count of items that passed this station today, or null when unknown. */
    countToday: z.number().int().nullable(),
})
export type FlowStation = z.infer<typeof flowStationSchema>

export const FLOW_ITEM_LEVELS = ['normal', 'attention', 'problem', 'done'] as const
export const flowItemSchema = z.object({
    /** Opaque id, stable while the item moves between stations. */
    id: z.string(),
    /** Short label: first name, short protocol ("1C23") or a topic. Never CPF, e-mail or phone. */
    label: z.string(),
    stationId: z.string(),
    level: z.enum(FLOW_ITEM_LEVELS),
    /** ISO time it reached the current station. */
    since: z.string(),
})
export type FlowItem = z.infer<typeof flowItemSchema>

export const agentFlowSchema = z.object({
    agentSlug: z.string(),
    /** e.g. "Aguardando ficha", "Consultando CRM · Lucas", "Pausado". */
    statusText: z.string(),
    paused: z.boolean(),
    stations: z.array(flowStationSchema),
    /** Items currently in the flow (max 12 per agent). */
    items: z.array(flowItemSchema),
    /** Items handled today. */
    today: z.number().int(),
    /** Side metrics for the big map, e.g. { label: "Cobradas", value: "28" }. */
    metrics: z.array(z.object({ label: z.string(), value: z.string() })),
    /** Systems touched by this flow, shown around the big map (connection slugs). */
    systems: z.array(z.string()),
    /** "live" when built from real systems, "example" otherwise. */
    dataMode: z.enum(['example', 'live']),
})
export type AgentFlow = z.infer<typeof agentFlowSchema>

/* ── Phase 7: "Pedir um novo agente" ─────────────────────────────────────────
   The panel never creates agents (the engine does, from AGENTS.md + skill reviewed by PR): it only records the
   request for whoever runs the engine. Free text is scrubbed on the server (no CPF, e-mail, phone, card or URL). */

export const AGENT_REQUEST_URGENCIES = ['baixa', 'normal', 'alta'] as const
export type AgentRequestUrgency = (typeof AGENT_REQUEST_URGENCIES)[number]

export const AGENT_REQUEST_STATUSES = ['recebido', 'em-analise', 'criado', 'recusado'] as const
export type AgentRequestStatus = (typeof AGENT_REQUEST_STATUSES)[number]

/** Systems a "create now" agent may use: nothing that charges, sends messages out, writes to a system or holds
    personal data (COG-007: those always go through review by PR). Server and form share this list. */
export const SIMPLE_AGENT_SYSTEMS = ['memoria', 'modelos-ia'] as const

/** "Criar agora" requests per day, for the whole ChildFund (Brasília day). The 6th gets 429 daily_limit. */
export const SIMPLE_AGENTS_PER_DAY = 5

export function isSimpleAgentSystem(slug: string): boolean {
    return (SIMPLE_AGENT_SYSTEMS as readonly string[]).includes(slug)
}

export const agentRequestInputSchema = z.object({
    name: z.string().trim().min(3).max(80),
    /** What the agent should do. */
    goal: z.string().trim().min(10).max(600),
    /** Connection slugs it will use (known catalog only, checked on the server). */
    systems: z.array(z.string().regex(/^[a-z0-9-]{1,80}$/)).max(10).default([]),
    /** One real example of the work, optional. */
    example: z.string().trim().max(600).optional(),
    urgency: z.enum(AGENT_REQUEST_URGENCIES).default('normal'),
    /** "Criar agora": a simple agent, no review; then every system must be in SIMPLE_AGENT_SYSTEMS. */
    createNow: z.boolean().default(false),
})
export type AgentRequestInput = z.input<typeof agentRequestInputSchema>

export const agentRequestSchema = z.object({
    /** Opaque id. */
    id: z.string(),
    name: z.string(),
    goal: z.string(),
    systems: z.array(z.string()),
    example: z.string().nullable(),
    urgency: z.enum(AGENT_REQUEST_URGENCIES),
    /** Asked as a simple agent, to be created without review. */
    createNow: z.boolean(),
    createdAt: z.string(),
    /** First name of who asked (the e-mail stays in the audit). */
    requestedBy: z.string(),
    /** True when the signed-in person made this request. */
    mine: z.boolean(),
    status: z.enum(AGENT_REQUEST_STATUSES),
    /** e.g. "Recebido", "Em análise". */
    statusLabel: z.string(),
})
export type AgentRequest = z.infer<typeof agentRequestSchema>

/* ── Phase 8: AI usage (tokens) and the agents org chart ──────────────────── */

export const USAGE_PERIODS = ['today', '7d', 'month'] as const
export type UsagePeriod = (typeof USAGE_PERIODS)[number]

export const tokenCountsSchema = z.object({
    input: z.number().int(),
    cachedInput: z.number().int(),
    output: z.number().int(),
    /** Estimated cost in BRL, or null when unknown. */
    costBRL: z.number().nullable(),
})
export type TokenCounts = z.infer<typeof tokenCountsSchema>

export const usageRowSchema = z.object({
    /** Agent slug, or "pergunte" (the panel's own Ask) or "leitura-fichas" (Live photo reading). */
    key: z.string(),
    label: z.string(),
    /** Francisco colour for agents, null for non-agent rows. */
    color: z.enum(AGENT_COLORS).nullable(),
    /** True for usage outside the engine (e.g. the Live photo reading). */
    external: z.boolean(),
    paused: z.boolean(),
    tokens: tokenCountsSchema,
    runs: z.number().int(),
})
export type UsageRow = z.infer<typeof usageRowSchema>

export const usageModelSchema = z.object({
    /** e.g. "gpt-5-mini". */
    model: z.string(),
    /** e.g. "Azure · ChildFund", "Live · Gemini". */
    provider: z.string(),
    tokens: tokenCountsSchema,
})
export type UsageModel = z.infer<typeof usageModelSchema>

export const usageSchema = z.object({
    period: z.enum(USAGE_PERIODS),
    /** e.g. "Outubro de 2026 · estimativa". */
    periodLabel: z.string(),
    totals: tokenCountsSchema,
    byAgent: z.array(usageRowSchema),
    byModel: z.array(usageModelSchema),
    /** Last 30 days, oldest first; total tokens per day per key (agent slug / "pergunte" / "leitura-fichas"). */
    daily: z.array(z.object({ date: z.string(), byKey: z.record(z.string(), z.number().int()) })),
    dataMode: z.enum(['example', 'live']),
})
export type Usage = z.infer<typeof usageSchema>

export type OrgNode = {
    /** Agent slug, or "team" for the people node. */
    id: string
    kind: 'team' | 'agent'
    name: string
    /** One-line role, e.g. "Lê fichas e cobra". */
    role: string
    color: AgentColor | null
    paused: boolean
    activity: AgentActivity | null
    /** Tasks handled today, or null. */
    tasksToday: number | null
    /** Systems used (connection slugs). */
    uses: string[]
    /** True while the node is example data. */
    example: boolean
    children: OrgNode[]
}

export const orgNodeSchema: z.ZodType<OrgNode> = z.lazy(() => z.object({
    id: z.string(),
    kind: z.enum(['team', 'agent']),
    name: z.string(),
    role: z.string(),
    color: z.enum(AGENT_COLORS).nullable(),
    paused: z.boolean(),
    activity: z.enum(AGENT_ACTIVITIES).nullable(),
    tasksToday: z.number().int().nullable(),
    uses: z.array(z.string()),
    example: z.boolean(),
    children: z.array(orgNodeSchema),
}))

export const orgChartSchema = z.object({
    root: orgNodeSchema,
    /** Dashed "consults" links, e.g. { from: "captacao-por-ficha", to: "memoria-childfund" }. */
    consults: z.array(z.object({ from: z.string(), to: z.string() })),
    dataMode: z.enum(['example', 'live']),
})
export type OrgChart = z.infer<typeof orgChartSchema>

/** Envelope: { data } on success, { error: { code } } on failure. */
/** How you can sign in: Microsoft (Entra), e-mail and password, or (dev only, neither set up) straight in. */
export const authOptionsSchema = z.object({ microsoft: z.boolean(), password: z.boolean(), dev: z.boolean() })
export type AuthOptions = z.infer<typeof authOptionsSchema>

export const passwordLoginSchema = z.object({ email: z.string().trim().min(3).max(200), password: z.string().min(1).max(200) })
export type PasswordLoginInput = z.infer<typeof passwordLoginSchema>

export const ERROR_CODES = ['session_required', 'forbidden', 'not_found', 'unavailable', 'invalid_request', 'limit_reached', 'daily_limit', 'invalid_credentials', 'conflict'] as const
export type ErrorCode = (typeof ERROR_CODES)[number]

/* Routes (all GET unless noted, all require the session except /auth/*):
   GET  /api/v1/me                      -> Me
   GET  /api/v1/auth/options            -> AuthOptions    (no session)
   POST /api/v1/auth/password           -> 204 + session cookie   body PasswordLoginInput (no session; same origin;
                                           401 invalid_credentials, 429 limit_reached after 5 misses in 15 min)
   GET  /api/v1/overview                -> Overview          (Início)
   GET  /api/v1/agents                  -> Agent[]           (Agentes)
   GET  /api/v1/agents/:slug/role       -> AgentRole
   GET  /api/v1/connections             -> Connection[]      (Conexões)
   POST /api/v1/connections/:slug/test  -> ConnectionTest
   GET  /api/v1/auth/login              -> redirect to Microsoft (or dev login when allowed)
   GET  /api/v1/auth/callback           -> sets the session, redirect to /
   POST /api/v1/auth/logout             -> 204
   GET  /healthz                        -> "ok" (no session)
   Phase 2 (POST need same-origin + session; every POST is written to the audit log):
   GET  /api/v1/pending                 -> PendingItem[]
   GET  /api/v1/pending/:id             -> PendingDetail
   POST /api/v1/pending/:id/approve     -> ActionResult   body DecisionInput
   POST /api/v1/pending/:id/reject      -> ActionResult   body DecisionInput
   POST /api/v1/pending/:id/return      -> ActionResult   body DecisionInput (note required)
   POST /api/v1/pending/:id/close       -> ActionResult   body DecisionInput (note required)
   POST /api/v1/agents/:slug/pause      -> Agent
   POST /api/v1/agents/:slug/resume     -> Agent
   Phase 3:
   GET  /api/v1/forms?status=&q=&page=  -> FormsPage      (10 per page)
   GET  /api/v1/forms/summary           -> FormsSummary
   GET  /api/v1/live?since=             -> Live           (screens poll every 5 s)
   Phase 4:
   GET  /api/v1/memory                  -> Memory
   POST /api/v1/ask                     -> Answer         body AskInput
   GET  /api/v1/ask/:ticket             -> AskReply       (phase 4b: the agent's reply to a question sent as a task)
   Phase 6:
   GET    /api/v1/memory/import                   -> MemoryImport
   POST   /api/v1/memory/documents                -> MemoryUploadItem  multipart: file, kind, title?   (audit)
   GET    /api/v1/memory/documents/:id            -> MemoryUploadItem  (poll while indexing)
   DELETE /api/v1/memory/documents/:id            -> ActionResult      (audit)
   POST   /api/v1/memory/batches                  -> MemoryBatch       body MemoryBatchCreate (audit; zip up to 1 GB, else 413)
   PUT    /api/v1/memory/batches/:id/parts/:n     -> MemoryBatchPart   raw bytes of part n (partBytes each; 409 conflict
                                                     when out of order: read the batch and go on from receivedBytes)
   POST   /api/v1/memory/batches/:id/finish       -> MemoryBatch       (all parts in: ready with folders and items, or failed)
   POST   /api/v1/memory/batches/:id/start        -> MemoryBatch       body MemoryBatchStart (audit)
   GET    /api/v1/memory/batches/:id              -> MemoryBatch       (poll while running)
   DELETE /api/v1/memory/batches/:id              -> MemoryBatch       (audit; files already imported stay)
   GET    /api/v1/connections/health              -> ConnectionHealth[]
   GET    /api/v1/connections/health/stream       -> text/event-stream (Server-Sent Events, session required, max 50 open):
            event `snapshot` data ConnectionHealth[] on connect; event `health` data ConnectionHealth after each check
            of that system; event `status` data { slug, ok, since } when a system flips ok <-> fail; ": ping" every 25 s
   GET    /api/v1/connections/:slug               -> ConnectionDetail
   POST   /api/v1/connections/:slug/checklist     -> ChecklistItem     body { text }        (audit)
   POST   /api/v1/connections/:slug/checklist/:id -> ChecklistItem     body { done }        (audit)
   GET    /api/v1/pending/:id/extras              -> PendingExtras
   GET    /api/v1/decisions?limit=                -> DecisionLogEntry[] (current user, from the audit)
   GET    /api/v1/live/extras                     -> LiveExtras
   Phase 6b:
   GET    /api/v1/live/flows                      -> AgentFlow[]   (all agents; screens poll every 5 s)
   GET    /api/v1/live/flows/:slug                -> AgentFlow
   Phase 7:
   GET    /api/v1/agent-requests                  -> AgentRequest[] (newest first, max 50)
   POST   /api/v1/agent-requests                  -> AgentRequest   body AgentRequestInput (audit; 20 open per person,
                                                     then 429 limit_reached)
   Phase 8:
   GET    /api/v1/usage?period=today|7d|month     -> Usage
   GET    /api/v1/agents/org                      -> OrgChart */
